import type { SnowplowClient } from '@nextmar/snowplow-sdk';
import logger from '@/utils/client-logger';
import type { Provider, TrackingEvent } from '../../types';
import { ProviderType } from '../../types';

/** ReportingCenter 使用的 SDK 薄 Provider，不实现队列、Schema、ID 或 tracker。 */
export class SnowplowSdkProvider implements Provider {
  /** ReportingCenter 的稳定 Provider 标识。 */
  readonly type = ProviderType.SNOWPLOW;

  /** ReportingCenter 过滤 Provider 时使用的最终远程解析开关。 */
  readonly isEnabled: boolean;

  /** 只有 SDK init 返回 ready 时才为 true。 */
  private initialized = false;

  /** 保存同一个 singleton Client 与明确开关。 */
  constructor(
    private readonly client: SnowplowClient,
    isEnabled: boolean,
  ) {
    this.isEnabled = isEnabled;
  }

  /** 初始化唯一 SDK Client，并把 ready 状态转换为 ReportingCenter boolean。 */
  async init(): Promise<boolean> {
    try {
      const result = await this.client.init();
      this.initialized = result.status === 'ready';
      return this.initialized;
    } catch {
      this.initialized = false;
      logger.error('[SnowplowSdkProvider] init_failed');
      return false;
    }
  }

  /** 将普通业务事件原样转交 SDK generic track，并隔离 SDK 异常。 */
  track(event: TrackingEvent): void {
    try {
      this.client.track(event.eventName as string, event.properties ?? {}, {
        sensorsEventId: event.eventId,
        omitPixel: event.options?.omitPixel,
      });
    } catch {
      logger.error('[SnowplowSdkProvider] track_failed');
    }
  }

  /** 返回 ReportingCenter 可消费的初始化状态。 */
  isInitialized(): boolean {
    return this.initialized;
  }
}
